/**
 * SubscriptionsList (T35.7.2.1) — the self-manage surface for the household's
 * support: the development-support donation + each backed idea. The single
 * canonical home is Settings → Billing & Subscriptions; the acquisition entry
 * points (donate banner, back-an-idea CTA) deep-link in.
 *
 * Per row: the derived status pill (from status + cadence), Change (reuses the
 * amount-ladder picker), reversible Cancel + Resume, and the dormant
 * "pay another way" switch (hidden with one registered processor). An empty
 * state and a support-total line frame the list.
 *
 * Cancel opens an OPTIONAL, non-blocking reason questionnaire — single-select +
 * an optional note revealed on "Other" + a one-tap skip. It NEVER stands
 * between the user and a completed cancellation (easy-cancel — the legal
 * constraint). Skipping cancels with no reason.
 *
 * Copy/UX is the prototype's (snapshot v-9, `docs/boards/dev/prototypes/
 * shopista-app/screens/app-settings.html`) carried verbatim, incl. the
 * legal-constrained wording. Token-only (CP5); mobile-first; ≥44px targets.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { Modal } from '../../components/Modal';
import { SubscriptionPicker } from './SubscriptionPicker';
import {
  listSubscriptions,
  changeSubscription,
  cancelSubscription,
  resumeSubscription,
  type SubscriptionResult,
  type RecurringSchedule,
  type CancellationReason,
} from './api';

/** The cadence labels the row shows (mirrors the proto's CAD_LBL). */
const CADENCE_LABEL: Record<RecurringSchedule, string> = {
  none: 'One-time',
  weekly: 'Weekly',
  biweekly: 'Every 2 weeks',
  monthly: 'Monthly',
};

const CADENCE_SUFFIX: Record<Exclude<RecurringSchedule, 'none'>, string> = {
  weekly: '/week',
  biweekly: '/2 weeks',
  monthly: '/month',
};

/** Normalize each cadence to a weekly multiplier for the support-total headline. */
const CADENCE_PER_WEEK: Record<Exclude<RecurringSchedule, 'none'>, number> = {
  weekly: 1,
  biweekly: 0.5,
  monthly: 12 / 52,
};

/** The cancel-reason options (single-select). Copy from the prototype's intent set. */
const REASON_OPTIONS: Array<{ value: CancellationReason; label: string }> = [
  { value: 'too_expensive', label: 'Too expensive' },
  { value: 'not_using', label: "I'm not using it" },
  { value: 'temporary', label: 'Just pausing for now' },
  { value: 'idea_resolved', label: 'The idea I backed is done' },
  { value: 'technical_issue', label: 'Something’s not working' },
  { value: 'other', label: 'Other' },
];

type StatusPill = { key: string; label: string };

/** Status label derives from (status, cadence) — no new status (see T35.7.2.1). */
export function deriveStatusPill(status: string, recurring: RecurringSchedule): StatusPill {
  if (status === 'canceled') return { key: 'canceled', label: 'Canceled' };
  if (status === 'failed') return { key: 'failed', label: 'Payment failed' };
  if (recurring === 'none') {
    return status === 'active'
      ? { key: 'paid', label: 'Paid' }
      : { key: 'scheduled', label: 'Scheduled' };
  }
  return { key: 'active', label: 'Active' };
}

/**
 * BUG34 — the Settings → Subscriptions surface shows ONLY rows that reached a
 * charged state: `active` (subscribed now) or `canceled` (was charged, now
 * stopped — kept so it can be Resumed). A pre-charge `recorded` intent (the
 * phantom row) and a `failed` attempt never surface. The backend read already
 * excludes them; this client filter is the matching guard so a stale cache or a
 * direct caller can't slip an uncharged row into the list or the summary.
 */
export function isVisibleSubscription(s: SubscriptionResult): boolean {
  return s.status === 'active' || s.status === 'canceled';
}

/** The weekly support total + the live (non-canceled) item count. */
export function supportSummary(subs: SubscriptionResult[]): { weekly: number; liveCount: number } {
  const visible = subs.filter(isVisibleSubscription);
  const live = visible.filter((s) => s.status !== 'canceled');
  const weekly = visible.reduce((sum, s) => {
    if (s.status === 'canceled' || s.recurring === 'none') return sum;
    return sum + (s.amount_cents / 100) * (CADENCE_PER_WEEK[s.recurring] ?? 0);
  }, 0);
  return { weekly, liveCount: live.length };
}

function amountText(s: SubscriptionResult): string {
  const dollars = Math.round(s.amount_cents / 100);
  return s.recurring === 'none' ? `$${dollars}` : `$${dollars}${CADENCE_SUFFIX[s.recurring]}`;
}

// --- styles (token-only) -----------------------------------------------------

const introStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  lineHeight: 1.45,
  marginBottom: 'var(--space-3)',
};

const totalStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-secondary)',
  backgroundColor: 'var(--color-accent-subtle)',
  border: '0.0625rem solid var(--color-accent-secondary)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-2) var(--space-3)',
  margin: '0 0 var(--space-3)',
  lineHeight: 1.45,
};

const rowStyle: CSSProperties = {
  border: '0.0625rem solid var(--color-border-subtle)',
  borderRadius: 'var(--radius-lg)',
  background: 'var(--color-bg-primary)',
  boxShadow: 'var(--shadow-sm)',
  padding: 'var(--space-3)',
  marginBottom: 'var(--space-2)',
};

const rowTopStyle: CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' };

const artStyle: CSSProperties = {
  flex: '0 0 auto',
  width: '2.125rem',
  height: '2.125rem',
  borderRadius: 'var(--radius-pill)',
  background: 'var(--color-accent-subtle)',
  border: '0.0625rem solid var(--color-accent-secondary)',
  display: 'grid',
  placeItems: 'center',
  fontSize: 'var(--font-size-base)',
};

const kindStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: 'var(--color-accent-primary)',
};

const titleStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-semibold)',
  color: 'var(--color-text-primary)',
  lineHeight: 1.35,
  marginTop: 'var(--space-1)',
};

const metaStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 'var(--space-2)',
  marginTop: 'var(--space-2)',
};

const amtStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-primary)',
};

const cadStyle: CSSProperties = { fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' };

const pillStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderRadius: 'var(--radius-pill)',
  padding: '0.125rem var(--space-2)',
  color: 'var(--color-text-secondary)',
  border: '0.0625rem solid var(--color-border-subtle)',
};

const ctrlsStyle: CSSProperties = { display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap', marginTop: 'var(--space-3)' };

const btnStyle: CSSProperties = {
  border: '0.0625rem solid var(--color-border-strong)',
  background: 'var(--color-bg-primary)',
  borderRadius: 'var(--radius-pill)',
  padding: '0 var(--space-4)',
  minHeight: '2.75rem',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 'var(--font-weight-bold)',
  color: 'var(--color-text-secondary)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const resumeBtnStyle: CSSProperties = {
  ...btnStyle,
  borderColor: 'var(--color-accent-primary)',
  color: 'var(--color-accent-primary)',
  background: 'var(--color-accent-subtle)',
};

const canceledTagStyle: CSSProperties = { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' };

const emptyStyle: CSSProperties = { textAlign: 'center', padding: 'var(--space-6) var(--space-3) var(--space-3)' };

const footStyle: CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-muted)',
  marginTop: 'var(--space-4)',
  lineHeight: 1.5,
};

const reasonListStyle: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' };

function reasonBtnStyle(on: boolean): CSSProperties {
  const base: CSSProperties = {
    textAlign: 'left',
    border: '0.0625rem solid var(--color-border-strong)',
    background: 'var(--color-bg-primary)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-2) var(--space-3)',
    minHeight: '2.75rem',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };
  return on
    ? { ...base, borderColor: 'var(--color-accent-primary)', background: 'var(--color-accent-subtle)', color: 'var(--color-accent-primary)', fontWeight: 'var(--font-weight-bold)' }
    : base;
}

const noteInputStyle: CSSProperties = {
  width: '100%',
  minHeight: '4rem',
  border: '0.0625rem solid var(--color-border-strong)',
  borderRadius: 'var(--radius-md)',
  padding: 'var(--space-2) var(--space-3)',
  font: 'inherit',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  background: 'var(--color-bg-primary)',
  marginBottom: 'var(--space-3)',
  resize: 'vertical',
  boxSizing: 'border-box',
};

const cancelActionsStyle: CSSProperties = { display: 'flex', gap: 'var(--space-2)' };

const cancelBtnStyle: CSSProperties = {
  flex: 1,
  minHeight: '2.75rem',
  border: 0,
  background: 'var(--color-accent-primary)',
  color: 'var(--color-text-inverse)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

const skipBtnStyle: CSSProperties = {
  flex: '0 0 auto',
  minHeight: '2.75rem',
  border: '0.0625rem solid var(--color-border-strong)',
  background: 'var(--color-bg-primary)',
  color: 'var(--color-text-secondary)',
  borderRadius: 'var(--radius-md)',
  padding: '0 var(--space-4)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-bold)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
};

export function SubscriptionsList(): JSX.Element {
  const [subs, setSubs] = useState<SubscriptionResult[] | null>(null);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);

  // Change modal state.
  const [changeTarget, setChangeTarget] = useState<SubscriptionResult | null>(null);
  // Cancel questionnaire state.
  const [cancelTarget, setCancelTarget] = useState<SubscriptionResult | null>(null);
  const [reason, setReason] = useState<CancellationReason | null>(null);
  const [note, setNote] = useState('');

  async function reload(): Promise<void> {
    try {
      const rows = await listSubscriptions();
      setSubs(rows);
      setError(false);
    } catch {
      setError(true);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function confirmChange(amountCents: number, recurring: RecurringSchedule): Promise<void> {
    if (!changeTarget || busy) return;
    setBusy(true);
    try {
      await changeSubscription(changeTarget.id, amountCents, recurring);
      setChangeTarget(null);
      await reload();
    } catch {
      /* leave the modal open; retry re-confirms */
    } finally {
      setBusy(false);
    }
  }

  function openCancel(sub: SubscriptionResult): void {
    setCancelTarget(sub);
    setReason(null);
    setNote('');
  }

  /** Cancel with the chosen reason (or none on skip). Never blocked on a reason. */
  async function doCancel(withReason: boolean): Promise<void> {
    if (!cancelTarget || busy) return;
    setBusy(true);
    try {
      const r = withReason ? reason : null;
      const n = withReason && reason === 'other' ? note.trim() || null : null;
      await cancelSubscription(cancelTarget.id, r, n);
      setCancelTarget(null);
      await reload();
    } catch {
      /* leave the questionnaire open */
    } finally {
      setBusy(false);
    }
  }

  async function doResume(sub: SubscriptionResult): Promise<void> {
    if (busy) return;
    setBusy(true);
    try {
      await resumeSubscription(sub.id);
      await reload();
    } catch {
      /* no-op; reload reflects the unchanged row */
    } finally {
      setBusy(false);
    }
  }

  if (subs === null && !error) {
    return (
      <div data-testid="subscriptions-list" aria-busy="true">
        <p style={introStyle}>Loading your support…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div data-testid="subscriptions-list">
        <p style={introStyle} data-testid="subscriptions-error">
          Couldn’t load your subscriptions. Please try again.
        </p>
      </div>
    );
  }

  // BUG34 — only charged rows reach the surface (see isVisibleSubscription).
  const rows = (subs ?? []).filter(isVisibleSubscription);
  const summary = supportSummary(rows);
  const summaryAmt = summary.weekly % 1 === 0 ? summary.weekly : summary.weekly.toFixed(2);

  return (
    <div data-testid="subscriptions-list">
      <p style={introStyle}>
        Everything you support here in one place — your <b>development support</b> and the{' '}
        <b>ideas you’ve backed</b>. Change the amount or cadence, or cancel anytime — canceling is
        reversible.
      </p>

      {rows.length === 0 ? (
        <div style={emptyStyle} data-testid="subscriptions-empty">
          <div aria-hidden="true" style={{ fontSize: 'var(--font-size-2xl)' }}>💜</div>
          <h4 style={{ margin: 'var(--space-3) 0 var(--space-1)', fontSize: 'var(--font-size-base)' }}>
            Nothing here yet
          </h4>
          <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
            When you support development or back an idea, it shows up here — change or cancel it
            anytime.
          </p>
        </div>
      ) : (
        <>
          <div style={totalStyle} data-testid="subscriptions-total">
            You’re supporting Shopista <b>${summaryAmt}/week</b> —{' '}
            {summary.liveCount === 1 ? '1 item' : `${summary.liveCount} items`}
          </div>

          {rows.map((s) => {
            const pill = deriveStatusPill(s.status, s.recurring);
            const canceled = s.status === 'canceled';
            const isDonation = s.type === 'donation';
            const kind = isDonation ? 'Development support' : 'Backed idea';
            const titleText = isDonation
              ? 'Shopista development'
              : s.featureidea_title ?? 'A backed idea';
            return (
              <div key={s.id} style={rowStyle} data-testid={`subscription-row-${s.id}`}>
                <div style={rowTopStyle}>
                  <span style={artStyle} aria-hidden="true">
                    {isDonation ? '💜' : '💡'}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={kindStyle}>{kind}</div>
                    <div style={titleStyle}>{titleText}</div>
                    <div style={metaStyle}>
                      <span style={amtStyle}>{amountText(s)}</span>
                      <span style={cadStyle}>{CADENCE_LABEL[s.recurring]}</span>
                      <span style={pillStyle} data-testid={`subscription-status-${s.id}`} data-status={pill.key}>
                        {pill.label}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={ctrlsStyle}>
                  {canceled ? (
                    <>
                      <span style={canceledTagStyle}>Canceled — you won’t be charged again.</span>
                      {s.recurring !== 'none' && (
                        <button
                          type="button"
                          style={resumeBtnStyle}
                          disabled={busy}
                          onClick={() => void doResume(s)}
                          data-testid={`subscription-resume-${s.id}`}
                        >
                          Resume
                        </button>
                      )}
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        style={btnStyle}
                        disabled={busy}
                        onClick={() => setChangeTarget(s)}
                        data-testid={`subscription-change-${s.id}`}
                      >
                        Change
                      </button>
                      <button
                        type="button"
                        style={btnStyle}
                        disabled={busy}
                        onClick={() => openCancel(s)}
                        data-testid={`subscription-cancel-${s.id}`}
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
                {/* "Pay another way" — dormant with one registered processor (V1).
                    Hidden until ≥2 eligible processors exist; mounted for .3. */}
                <div hidden data-testid={`subscription-pay-another-${s.id}`} />
              </div>
            );
          })}
        </>
      )}

      <p style={footStyle}>
        Shopista is an independent, for-profit product — your support is not a donation to a charity
        and isn’t tax-deductible. Backing an idea signals what matters to you; it never buys the
        roadmap or guarantees a feature.
      </p>

      {/* Change — reuses the amount-ladder picker, prefilled. */}
      <Modal
        open={changeTarget !== null}
        onClose={() => setChangeTarget(null)}
        title={changeTarget?.type === 'donation' ? 'Change your support' : 'Change this idea'}
        ariaLabel="Change subscription"
        variant="sheet"
      >
        {changeTarget && (
          <div data-testid="subscription-change-sheet">
            <SubscriptionPicker
              open={changeTarget !== null}
              confirmLabel="Save"
              busy={busy}
              onClose={() => setChangeTarget(null)}
              onConfirm={(amountCents, recurring) => void confirmChange(amountCents, recurring)}
            />
          </div>
        )}
      </Modal>

      {/* Cancel — the OPTIONAL, non-blocking reason questionnaire. */}
      <Modal
        open={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        title="Before you go"
        ariaLabel="Cancel subscription"
        variant="sheet"
      >
        {cancelTarget && (
          <div data-testid="cancel-questionnaire">
            <p style={introStyle}>
              You can cancel right away. If you’d like, tell us why — it helps us improve. This is
              optional.
            </p>
            <div style={reasonListStyle} role="group" aria-label="Reason for canceling">
              {REASON_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  style={reasonBtnStyle(reason === o.value)}
                  aria-pressed={reason === o.value}
                  data-testid={`cancel-reason-${o.value}`}
                  onClick={() => setReason(o.value)}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {reason === 'other' && (
              <textarea
                style={noteInputStyle}
                placeholder="Tell us more (optional)"
                aria-label="Anything else (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                data-testid="cancel-note"
              />
            )}

            <div style={cancelActionsStyle}>
              <button
                type="button"
                style={skipBtnStyle}
                disabled={busy}
                onClick={() => void doCancel(false)}
                data-testid="cancel-skip"
              >
                Skip
              </button>
              <button
                type="button"
                style={cancelBtnStyle}
                disabled={busy}
                onClick={() => void doCancel(reason !== null)}
                data-testid="cancel-confirm"
              >
                Cancel subscription
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
